import { extractErrorMessage } from "@/lib/error"
import { configureStore } from "@/lib/store"
import { authStore } from "@/store/auth"
import { AxiosError } from "axios"

import { pagarmeService } from "../services/pagarme-service"
import { stripeService } from "../services/stripe-service"
import type { PagarmeSubscriptionRequest, PagarmeSubscriptionResponse } from "../types"
import { Plan, SubscriptionStatusInfo } from "../types"

interface BillingState {
  subscriptionStatus: SubscriptionStatusInfo | null
  isLoading: boolean
  error: string | null
  paymentError: string | null
  selectedPlan: Plan | null
  clientSecret: string | null
  isSubscriber: boolean | null // Atributo síncrono derivado do subscriptionStatus
  isSubscriptionActive: boolean | null // Atributo síncrono derivado do subscriptionStatus
  isSubscriptionOverdue: boolean | null // Atributo síncrono - true se está em atraso (passou do period_end mas ainda no período de tolerância)
  isSubscriptionExpired: boolean | null // Atributo síncrono derivado do subscriptionStatus ou created_at do user
  fetchSubscriptionStatus: () => Promise<SubscriptionStatusInfo>
  setSubscriptionStatus: (status: SubscriptionStatusInfo) => void
  selectPlan: (plan: Plan | null) => void
  submitCheckoutSuccess: (data: { session_id: string }) => Promise<any>
  createTransparentCheckout: (
    data: {
      plan_id: string
      interval: "monthly" | "yearly"
      trial_days?: number
      customer_info: {
        email: string
        name: string
        phone?: string
      }
      payment_method_id?: string
      success_url?: string
      cancel_url?: string
    },
    directSubscription?: boolean
  ) => Promise<any>

  confirmSubscription: (paymentMethodId: string) => Promise<any>
  checkSubscriptionActive: () => Promise<boolean>
  checkIsSubscriber: () => Promise<boolean>

  // Pagar.me actions
  createPagarmeSubscription: (
    data: PagarmeSubscriptionRequest
  ) => Promise<PagarmeSubscriptionResponse>
  cancelPagarmeSubscription: (reason?: string) => Promise<void>
  updatePagarmePaymentMethod: (cardToken: string) => Promise<void>
}

// Deriva se a assinatura está ativa (active e dentro do período válido)
const deriveIsSubscriptionActive = (status: SubscriptionStatusInfo | null): boolean => {
  if (status?.status !== "active") return false

  if (status.current_period_end) {
    const periodEnd = new Date(status.current_period_end)
    if (new Date() > periodEnd) return false
  }

  return true
}

// Deriva se a assinatura está em atraso (passou do period_end mas ainda dentro do período de tolerância)
const deriveIsSubscriptionOverdue = (status: SubscriptionStatusInfo | null): boolean => {
  if (!status?.current_period_end) {
    return false
  }

  const now = new Date()
  const GRACE_PERIOD_MS = 5 * 24 * 60 * 60 * 1000 // 5 dias em milissegundos
  const periodEnd = new Date(status.current_period_end)
  const gracePeriodEnd = new Date(periodEnd.getTime() + GRACE_PERIOD_MS)

  // Está em atraso se: passou do period_end MAS ainda não passou do período de tolerância
  return now > periodEnd && now <= gracePeriodEnd
}

// Deriva se a assinatura/trial expirou
const deriveIsSubscriptionExpired = (status: SubscriptionStatusInfo | null): boolean => {
  // Partner organizations are exempt from any expiration check.
  if (authStore.getState().user?.is_partner) {
    return false
  }

  const now = new Date()
  const GRACE_PERIOD_DAYS = 5 * 24 * 60 * 60 * 1000 // Período de tolerância para assinantes - 5 dias
  const TRIAL_PERIOD_DAYS = 7 * 24 * 60 * 60 * 1000 // Período de trial para novos usuários - 7 dias

  // Se tem subscription com current_period_end, verifica se passou o período de tolerância (5 dias)
  if (status?.current_period_end) {
    const periodEnd = new Date(status.current_period_end)
    const gracePeriodEnd = new Date(periodEnd.getTime() + GRACE_PERIOD_DAYS)
    if (gracePeriodEnd < now) {
      return true
    }
  }

  // Se não tem subscription, verifica se o trial de 7 dias expirou baseado no created_at do usuário
  if (!status || !status.current_period_end) {
    const user = authStore.getState().user
    if (user?.created_at) {
      const createdAt = new Date(user.created_at)
      const trialEndDate = new Date(createdAt.getTime() + TRIAL_PERIOD_DAYS)
      if (trialEndDate < now) {
        return true
      }
    }
  }

  return false
}

export const useBillingStore = configureStore<BillingState>((set, get) => ({
  subscriptionStatus: null,
  isLoading: false,
  error: null,
  paymentError: null,
  selectedPlan: null,
  clientSecret: null,
  isSubscriber: null,
  isSubscriptionActive: null,
  isSubscriptionOverdue: null,
  isSubscriptionExpired: null,

  fetchSubscriptionStatus: async () => {
    set({ isLoading: true, error: null })

    try {
      const status = await stripeService.getSubscriptionStatus()
      set({
        subscriptionStatus: status,
        isLoading: false,
        isSubscriber: true,
        isSubscriptionActive: deriveIsSubscriptionActive(status),
        isSubscriptionOverdue: deriveIsSubscriptionOverdue(status),
        isSubscriptionExpired: deriveIsSubscriptionExpired(status),
      })
      return status
    } catch (error) {
      // Se for 404, significa que não há assinatura
      if (error instanceof AxiosError && error.response?.status === 404) {
        set({
          subscriptionStatus: null,
          isSubscriber: false,
          isSubscriptionActive: false,
          isSubscriptionOverdue: false,
          isSubscriptionExpired: deriveIsSubscriptionExpired(null),
          isLoading: false,
        })
        return null as unknown as SubscriptionStatusInfo
      }
      console.error(error)
      const errorMessage = extractErrorMessage(error, "Falha ao buscar status da assinatura")
      set({ error: errorMessage, isLoading: false })
      throw error
    }
  },

  setSubscriptionStatus: (status: SubscriptionStatusInfo) => {
    set({
      subscriptionStatus: status,
      isSubscriber: true,
      isSubscriptionActive: deriveIsSubscriptionActive(status),
      isSubscriptionOverdue: deriveIsSubscriptionOverdue(status),
      isSubscriptionExpired: deriveIsSubscriptionExpired(status),
    })
  },

  checkSubscriptionActive: async (): Promise<boolean> => {
    const subscriptionStatus = await get().fetchSubscriptionStatus()
    return subscriptionStatus?.status === "active" || subscriptionStatus?.status === "trialing"
  },

  checkIsSubscriber: async (): Promise<boolean> => {
    const subscriptionStatus = await get().fetchSubscriptionStatus()
    return (subscriptionStatus && subscriptionStatus?.status !== "trialing") || false
  },

  selectPlan: (plan: Plan | null) => {
    set({ selectedPlan: plan })
  },

  createTransparentCheckout: async (data, directSubscription = false) => {
    set({ isLoading: true, error: null, paymentError: null })

    try {
      let result
      if (directSubscription) {
        result = await stripeService.createDirectSubscription(data)
      } else {
        result = await stripeService.createCheckoutSession(data)
      }

      if (!result.client_secret) {
        throw new Error("No client secret returned from API")
      }

      set({ clientSecret: result.client_secret, isLoading: false })
      return result
    } catch (error) {
      console.error(error)
      const errorMessage = extractErrorMessage(error, "Falha ao criar sessão de checkout")
      set({ paymentError: errorMessage, isLoading: false })
      throw error
    }
  },

  submitCheckoutSuccess: async (data: { session_id: string }) => {
    set({ isLoading: true, error: null, paymentError: null })
    try {
      const result = await stripeService.submitCheckoutSuccess(data)
      set({ isLoading: false })
      return result
    } catch (error) {
      console.error(error)
      const errorMessage = extractErrorMessage(error, "Falha ao criar sessão de checkout")
      set({ paymentError: errorMessage, isLoading: false })
      throw error
    }
  },

  confirmSubscription: async (paymentMethodId: string) => {
    set({ isLoading: true, error: null, paymentError: null })

    try {
      const result = await stripeService.confirmSubscription(paymentMethodId)
      set({ isLoading: false })
      return result
    } catch (error) {
      console.error(error)
      const errorMessage = extractErrorMessage(error, "Falha ao confirmar assinatura")
      set({ paymentError: errorMessage, isLoading: false })
      throw error
    }
  },

  // Pagar.me actions
  createPagarmeSubscription: async (data: PagarmeSubscriptionRequest) => {
    set({ isLoading: true, error: null, paymentError: null })

    try {
      const result = await pagarmeService.createSubscription(data)
      set({ isLoading: false })

      // Refresh subscription status after creating
      get().fetchSubscriptionStatus()

      return result
    } catch (error) {
      console.error(error)
      const errorMessage = extractErrorMessage(error, "Falha ao criar assinatura via Pagar.me")
      set({ paymentError: errorMessage, isLoading: false })
      throw error
    }
  },

  cancelPagarmeSubscription: async (reason?: string) => {
    set({ isLoading: true, error: null })

    try {
      await pagarmeService.cancelSubscription(reason)
      set({ isLoading: false })
      await get().fetchSubscriptionStatus()
    } catch (error) {
      console.error(error)
      const errorMessage = extractErrorMessage(error, "Falha ao cancelar assinatura")
      set({ error: errorMessage, isLoading: false })
      throw error
    }
  },

  updatePagarmePaymentMethod: async (cardToken: string) => {
    set({ isLoading: true, error: null })

    try {
      await pagarmeService.updatePaymentMethod(cardToken)
      set({ isLoading: false })
      await get().fetchSubscriptionStatus()
    } catch (error) {
      console.error(error)
      const errorMessage = extractErrorMessage(error, "Falha ao atualizar método de pagamento")
      set({ error: errorMessage, isLoading: false })
      throw error
    }
  },
}))
